import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { FC, ReactElement } from 'react'
import styled from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import { Link } from '@ta-interaktiv/contentbricks'

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]

  const Zuerich = 'Zürich'
  const Bern = 'Bern'
  const Basel = 'Basel'

  const weatherStationZuerich = 'Zürich-Fluntern'
  const weatherStationBern = 'Bern-Zollikofen'
  const weatherStationBasel = 'Basel-Binningen'

  let clientTown = Zuerich
  let clientWeatherStation = weatherStationZuerich

  if (tenant) {
    // console.log('tenant.key:', tenant.key)
    switch (tenant.key) {
      case 'bazonline':
        clientTown = Basel
        clientWeatherStation = weatherStationBasel
        break

      case 'derbund':
      case 'bernerzeitung':
      case 'berneroberlaender':
      case 'thunertagblatt':
      case 'langenthalertagblatt':
        clientTown = Bern
        clientWeatherStation = weatherStationBern
        break
      default:
        clientTown = Zuerich
        clientWeatherStation = weatherStationZuerich
    }
  }

  return (
    <AppContainer className={globalClasses.join(' ')}>
      <div className='ui vertical fitted attached segment' id='methodik'>
        <div
          className='ui text container methodik'
          style={{ position: 'relative', marginBottom: '2em' }}
        >
          <h3>Methodik</h3>

          <p>
            Die oben berechneten Werte basieren auf den langjährigen Messreihen
            von Meteo Schweiz und den{' '}
            <Link href='https://www.nccs.admin.ch/nccs/de/home/klimawandel-und-auswirkungen/schweizer-klimaszenarien.html'>
              Klimaszenarien CH2018
            </Link>{' '}
            des Bundes. Die Grundlage der Klimaszenarien wiederum bilden
            Simulationen mit 21 verschiedenen Computermodellen, die an
            europäischen Forschungs&shy;institutionen betrieben werden.
            Berücksichtigt werden dabei zwei mögliche Entwicklungen der
            Treibhausgas&shy;emissionen:
          </p>
          <div className='list'>
            <ul>
              <li>
                <b>Weiter wie bisher</b>: <br />
                Das Wirtschaftswachstum wird durch die Verbrennung von Öl, Kohle
                und Gas angetrieben. Die Menschheit ergreift keine verstärkten
                Klimaschutz&shy;massnahmen, fährt trotz des technischen
                Fortschritts so weiter wie bis anhin und emittiert immer mehr
                Treibhausgase. Bei diesem pessimistischen Szenario (RCP 8.5)
                steigt die globale Mitteltemperatur bis Ende des Jahrhunderts um
                etwa 4,8 Grad im Vergleich mit dem vorindustriellen Zustand.
              </li>
              <br />
              <li>
                <b>Verstärkter Klimaschutz</b>: <br />
                Die Weltgemeinschaft einigt sich darauf, den Ausstoss von
                Treibhausgasen drastisch zu reduzieren. Mit einer umgehend
                eingeleiteten Senkung der Emissionen auf praktisch null wird der
                Anstieg der Treibhausgas&shy;konzentration in der Atmosphäre bis
                etwa in 20 Jahren gestoppt. Bei diesem optimistischen Szenario
                (RCP 2.6) lässt sich die globale Erwärmung auf 2 Grad Celsius
                gegenüber dem vorindustriellen Zustand begrenzen.
              </li>
            </ul>
          </div>

          {clientTown === Zuerich && (
            <p>
              Die Auswirkungen der beiden Szenarien unterscheiden sich enorm. So
              werden im optimistischen Fall im Jahr 2060 nur durchschnittlich
              2,3 Tropennächte pro Jahr in Zürich verzeichnet. Im
              pessimistischen Fall wären es fast viermal so viele. Zur
              Einordnung: Die Werte für Zürich beziehen sich auf die
              Wetterstation Fluntern, die am Zürichberg auf 570 Metern liegt.
              Eine Temperatur, die hier gemessen wird, lässt sich oft nicht auf
              das Zentrum übertragen, wo der «Wärmeinsel-Effekt» für weitaus
              mehr Tropennächte sorgt.
            </p>
          )}

          {clientTown === Bern && (
            <p>
              Die Auswirkungen der beiden Szenarien unterscheiden sich enorm. So
              werden im optimistischen Fall im Jahr 2060 nur durchschnittlich
              0,5 Tropennächte pro Jahr in Bern verzeichnet. Im pessimistischen
              Fall wären es sechsmal so viele. Zur Einordnung: Die Werte für
              Bern beziehen sich auf die Wetterstation Zollikofen, die
              ausserhalb der Stadt im Grünen liegt. Eine Temperatur, die hier
              gemessen wird, lässt sich oft nicht auf das Zentrum übertragen, wo
              der «Wärmeinsel-Effekt» für weitaus mehr Tropennächte sorgt.
            </p>
          )}

          {clientTown === Basel && (
            <p>
              Die Auswirkungen der beiden Szenarien unterscheiden sich enorm. So
              werden im optimistischen Fall im Jahr 2060 nur durchschnittlich
              2,3 Tropennächte pro Jahr in Basel verzeichnet. Im pessimistischen
              Fall wären es fast viermal so viele. Zur Einordnung: Die Werte für
              Basel beziehen sich auf die Wetterstation Binningen, die
              ausserhalb des Zentrums im Grünen liegt. Eine Temperatur, die hier
              gemessen wird, lässt sich oft nicht auf das Stadtinnere
              übertragen, wo der «Wärmeinsel-Effekt» für weitaus mehr
              Tropennächte sorgt.
            </p>
          )}
          <p>
            Alle dargestellten Zahlen zeigen den Median, der am ehesten dem
            absehbaren Wert entspricht und im Rahmen der Klimaszenarien als
            «erwartet» bezeichnet wird. Wenn beispielsweise für 2060 acht
            Tropennächte prognostiziert werden, dann liegt je die Hälfte der
            Modellresultate über respektive unter diesem Wert.
          </p>

          <p>
            Es handelt sich immer um Durchschnittswerte der
            Klima&shy;verhältnisse über einen Zeitraum von drei Jahrzehnten. Mit
            «2060» sind also die Jahre 2045 bis 2074 gemeint. «2085» bezieht
            sich auf die Jahre 2070 bis 2099. Den Prognosen wird der gemessene
            durchschnittliche Wert von 1991 bis 2020 gegenübergestellt. Dieser
            Zeitraum ist die gegenwärtige Norm des Schweizer Klimas und wird im
            Text oben mit «Heute» bezeichnet.
          </p>
          <p>
            Natürlich werden auch in Zukunft einzelne Jahre von den erwarteten
            Durchschnittswerten abweichen. Wie wir es bereits heute kennen, sind
            in jedem Jahr einzelne Klimaphänomene schwächer oder stärker
            ausgeprägt als im langjährigen Mittel. Der aktuelle Sommer etwa
            liegt – abgesehen vom Niederschlag – bei allen Indikatoren deutlich
            über der Normperiode von 1991 bis 2020.
          </p>
        </div>
      </div>
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)<{ tenant?: string }>`
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: var(--font-sans-serif);
  border: 1px solid transparent;
  color: var(--text-color);
  -moz-osx-font-smoothing: grayscale;

  @media only screen and (max-width: 599px) {
    .ui.container {
      margin-left: 2em !important;
      margin-right: 2em !important;
    }
  }

  #methodik {
    margin-bottom: 3em;
  }

  .text.container.first {
    margin-bottom: 8em;
  }

  p {
    font-family: var(--font-serif);
    font-weight: 100;
    font-size: 18px;
    line-height: 1.6em;
  }

  .methodik {
    padding-top: 4em;
    h3 {
      font-weight: 800;
    }
    p,
    li {
      font-size: 1.6em;
    }
  }
`
export default MainComponent
